import React, { useEffect, useMemo, useState } from "react";
import { decodeCompare, type CompareOp, type TaktAttribute } from "../../filtering/attributes";
import "./FacetSection.css";

interface Props {
  attr: TaktAttribute;
  selected: string[];
  /** value id → matching activity count (enum/boolean facets). */
  counts: Record<string, number>;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleValue: (valueId: string) => void;
  onSetCompare: (op: CompareOp, value: number | null) => void;
  onClear: () => void;
  /** Navigate to a value (e.g. scroll to a section). Set for navigable attrs. */
  onNavigate?: (valueId: string) => void;
  /** Optional action shown in the facet header (e.g. "Manage trades"). */
  headerLink?: { label: string; onClick: () => void };
}

/**
 * One filter dimension in the View rail, rendered by KIND so the interaction
 * fits the data:
 *   boolean (e.g. Critical path) → a single inline toggle, no expand.
 *   range   (e.g. Total float)   → an inline operator + value, no expand.
 *   enum    (Section/Trade/code) → a collapsible value list (chips or search).
 */
const FacetSection: React.FC<Props> = ({
  attr,
  selected,
  counts,
  expanded,
  onToggleExpand,
  onToggleValue,
  onSetCompare,
  onClear,
  onNavigate,
  headerLink,
}) => {
  const [search, setSearch] = useState("");
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Compare control state (range), kept in sync with the encoded filter.
  const [op, setOp] = useState<CompareOp>("eq");
  const [num, setNum] = useState("");
  const encoded = selected[0] || "";
  useEffect(() => {
    if (attr.kind !== "range") return;
    const cmp = decodeCompare(encoded);
    if (!cmp) {
      setNum("");
    } else {
      setOp(cmp.op);
      setNum(String(cmp.value));
    }
  }, [encoded, attr.kind]);

  const applyCompare = (nextOp: CompareOp, nextNum: string) => {
    if (nextNum === "" || Number.isNaN(Number(nextNum))) {
      onSetCompare(nextOp, null);
      return;
    }
    onSetCompare(nextOp, Number(nextNum));
  };

  // ----- Inline: boolean → one toggle row -----
  if (attr.kind === "boolean") {
    const v = attr.domain[0];
    const on = selectedSet.has(v.id);
    const count = counts[v.id] ?? 0;
    return (
      <div className="facet-section facet-section--inline">
        <button
          type="button"
          className={`facet-inline-row facet-inline-toggle${on ? " facet-inline-toggle--on" : ""}`}
          onClick={() => onToggleValue(v.id)}
          disabled={count === 0 && !on}
          aria-pressed={on}
        >
          <span className="facet-section__title">{attr.label}</span>
          <span className="facet-count">{count}</span>
          <span className="facet-switch" aria-hidden />
        </button>
      </div>
    );
  }

  // ----- Inline: range → label + compare control -----
  if (attr.kind === "range") {
    return (
      <div className="facet-section facet-section--inline">
        <div className="facet-inline-row">
          <span className="facet-section__title">{attr.label}</span>
          <div className="facet-range">
            <select
              className="facet-range__op"
              value={op}
              onChange={(e) => {
                const o = e.target.value as CompareOp;
                setOp(o);
                applyCompare(o, num);
              }}
            >
              <option value="eq">=</option>
              <option value="gt">&gt;</option>
              <option value="lt">&lt;</option>
            </select>
            <input
              type="number"
              className="facet-range__input"
              placeholder="value"
              value={num}
              onChange={(e) => {
                setNum(e.target.value);
                applyCompare(op, e.target.value);
              }}
            />
            {attr.unit && <span className="facet-range__unit">{attr.unit}</span>}
          </div>
        </div>
      </div>
    );
  }

  // ----- Collapsible: enum (searchable list or chips) -----
  const activeCount = selected.length;
  let body: React.ReactNode;
  if (attr.searchable) {
    const q = search.trim().toLowerCase();
    const values = q
      ? attr.domain.filter(
          (v) =>
            v.label.toLowerCase().includes(q) ||
            (v.code ? v.code.toLowerCase().includes(q) : false),
        )
      : attr.domain;
    body = (
      <>
        <input
          className="facet-search"
          placeholder={`Search ${attr.label.toLowerCase()}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="facet-list">
          {values.map((v) => {
            const sel = selectedSet.has(v.id);
            const count = counts[v.id] ?? 0;
            const disabled = count === 0 && !sel;
            return (
              <button
                key={v.id}
                type="button"
                className={`facet-row${disabled ? " facet-row--disabled" : ""}`}
                onClick={() => !disabled && onToggleValue(v.id)}
              >
                <span className={`facet-check${sel ? " facet-check--on" : ""}`} />
                {v.fill && (
                  <span className="facet-swatch" style={{ background: v.fill }} />
                )}
                <span className="facet-row-label">{v.label}</span>
                {v.code && v.code !== v.label && (
                  <span className="facet-row-code">{v.code}</span>
                )}
                <span className="facet-count">{count}</span>
                {attr.navigable && onNavigate && (
                  <span
                    role="button"
                    tabIndex={0}
                    className="facet-row-jump"
                    title={`Jump to ${v.label}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!disabled) onNavigate(v.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        if (!disabled) onNavigate(v.id);
                      }
                    }}
                  >
                    →
                  </span>
                )}
              </button>
            );
          })}
          {values.length === 0 && <div className="facet-empty">No matches</div>}
        </div>
      </>
    );
  } else {
    body = (
      <div className="facet-chips">
        {attr.domain.map((v) => {
          const sel = selectedSet.has(v.id);
          const count = counts[v.id] ?? 0;
          const disabled = count === 0 && !sel;
          const style =
            sel && v.fill
              ? { background: v.fill, color: v.font || "#fff", borderColor: v.fill }
              : undefined;
          return (
            <button
              key={v.id}
              type="button"
              className={
                "facet-chip" +
                (sel ? " facet-chip--on" : "") +
                (disabled ? " facet-chip--disabled" : "")
              }
              style={style}
              onClick={() => !disabled && onToggleValue(v.id)}
            >
              {!sel && v.fill && (
                <span className="facet-chip__dot" style={{ background: v.fill }} />
              )}
              <span className="facet-chip__label">{v.label}</span>
              <span className={`facet-chip__count${sel ? " facet-chip__count--on" : ""}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`facet-section${expanded ? " facet-section--open" : ""}`}>
      <button
        type="button"
        className="facet-section__head"
        onClick={onToggleExpand}
        aria-expanded={expanded}
      >
        <span className="facet-section__chev">▸</span>
        <span className="facet-section__title">{attr.label}</span>
        {activeCount > 0 && <span className="facet-section__badge">{activeCount}</span>}
        <span className="facet-section__spacer" />
        {headerLink && (
          <span
            role="button"
            tabIndex={0}
            className="facet-section__link"
            onClick={(e) => {
              e.stopPropagation();
              headerLink.onClick();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                headerLink.onClick();
              }
            }}
          >
            {headerLink.label}
          </span>
        )}
        {activeCount > 0 && (
          <span
            role="button"
            tabIndex={0}
            className="facet-section__clear"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                onClear();
              }
            }}
          >
            clear
          </span>
        )}
      </button>
      {expanded && <div className="facet-section__body">{body}</div>}
    </div>
  );
};

export default FacetSection;
